import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { projectBoardApi, useGetProjectQuery } from 'app/services/projectBoardApi';
import {
  toggleRailCollapsed,
  openWorkflow,
  closeWorkflow,
} from 'app/slices/projectBoardSlice';
import { useStylesheets } from 'hooks/useStylesheets';
import { usePopupKeys } from 'hooks/usePopupKeys';
import { getBootUserId } from 'hooks/useDashboardData';
import GlobalHeader from 'pages/dashboard/components/GlobalHeader';
import PageFooter from 'pages/dashboard/components/PageFooter';
import { useActualTabs } from './hooks/useProjectBoardData';
import { useGlobalInputBehaviors } from './hooks/useGlobalInputBehaviors';
import { TAB_TITLES, TAB_REOPEN_EVENT } from './projectBoardUtils';
import BoardMenu from './components/BoardMenu';
import BoardContainerHeader from './components/BoardContainerHeader';
import BoardTabs from './components/BoardTabs';
import WorkflowPopup from './components/workflow/WorkflowPopup';

// Project-board CSS scoped to this route (§1 boot: header.css + skeleton.css
// statically, common.css + styles.css at runtime). react.html already loads
// common.css + header.css globally; these two are injected after them so the
// cascade order (styles.css last) holds. NOTE styles.css bundles Bootstrap
// 3.3.4 — it MUST stay route-scoped exactly like the dashboard scopes the
// whiteboard CSS (parity 7).
const PROJECT_CSS = [
  '/static/css/project/skeleton.css',
  '/static/css/project/styles.css',
];

/**
 * Project Board page (/project/{id} — Backbone app `fn.app.name === 'project'`),
 * sub-phase 3a: shell + tab bar + hash routing + General Info form.
 *
 * Hash-fragment tabs: `/project/{id}#project` … `#report_logo` (no pushState;
 * back/forward re-fires — parity 19). On load `#ahus` is rewritten to
 * `#project` (parity 20). DELIBERATE DELTA (inventory risk 2 / parity 23):
 * a bare URL with NO hash defaults to `#project` — Backbone renders NO tab on
 * bare load (all inbound links carry #project); flagged for frontend-verify.
 *
 * Body className is wiped and set to the active tab name (changePrjAppViewState
 * parity 22); document.title = '{Tab title}: {project name lowercased}'
 * (parity 8). Project fetch error → redirect /dashboard (parity 5);
 * non-editable project → `non-editable-project` on #container (parity 6).
 */
const ProjectBoardPage = () => {
  const { projectId: projectIdParam } = useParams();
  const projectId = Number(projectIdParam);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const railCollapsed = useAppSelector((s) => s.projectBoard.railCollapsed);
  const workflowOpen = useAppSelector((s) => s.projectBoard.modals.workflow);

  // Gate first paint on the route CSS (skeleton/styles) loading — these sheets
  // are injected AFTER React mounts, and react.html only ships common.css +
  // header.css in <head>. Without the gate, the shared GlobalHeader popups
  // (Recent Reports History, User Menu, More Actions, Notifications) and the
  // project form paint as raw unstyled text in the gap before the <link>s
  // apply (FOUC). Legacy projectBase.html loads all CSS render-blocking in
  // <head>; holding a brief blank frame here mirrors that (parity).
  const cssLoaded = useStylesheets(PROJECT_CSS);
  // Global popup keyboard handling (Esc → Cancel + dismiss; Enter → autofocus
  // button; contentEditable-guarded) — MainView body.keyup parity (parity 35).
  usePopupKeys();
  // MainView delegated input behaviors (tab-to-next, dblclick select-all,
  // .textarea maxlength, number/english/phone keypress filters) — parity 26–34.
  useGlobalInputBehaviors();

  const { data: project, isError: projectError } = useGetProjectQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const actualTabs = useActualTabs(projectId);

  // ---- hash normalization (MainView initialize, runs once before routing) ----
  useEffect(() => {
    if (window.location.hash === '#ahus' || !window.location.hash) {
      navigate({ hash: '#project' }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Active tab from the hash; unroutable fragments (#partners/#accounting —
  // [NEG] dead plumbing, parity 24) activate nothing, like Backbone.
  const activeTab = useMemo(() => {
    const tab = location.hash.slice(1);
    return TAB_TITLES[tab] ? tab : null;
  }, [location.hash]);

  // body[data-user_id] (server-rendered shell parity, parity 4).
  useEffect(() => {
    const id = getBootUserId();
    if (id != null) document.body.setAttribute('data-user_id', String(id));
    return () => {
      document.body.removeAttribute('data-user_id');
    };
  }, []);

  // Height-chain parity (QA: page-level scrollbar). Legacy projectBase.html
  // chains html(100%) → body(100%, skeleton.css:126-133) → #ksdm-content
  // `calc(100% - 63px)` (skeleton.css:317-319); header is 58px in-flow and the
  // footer is absolute — the page itself never scrolls, panes scroll inside
  // (e.g. body.shipping .main-container{overflow:auto}). The React shell
  // breaks this twice (same as the AHU board, 6c1 finding 4):
  //  (1) #react-root has no height → skeleton's percentages resolve to auto;
  //  (2) the dashboard bundle leaks `body #ksdm-content{height:calc(100vh -
  //      53px)}` (StatusesRail.module.scss :global) which out-ranks skeleton's
  //      bare `#ksdm-content` — 58px header + (100vh − 53px) = 100vh + 5px of
  //      flow → a page-level vertical scrollbar on every project tab.
  // Route-scoped repair (AHU/Reports precedent): pin #react-root to 100% for
  // this route's lifetime, and carry skeleton.css's own value as an inline
  // style on #ksdm-content (below) so the leaked vh rule loses without
  // touching the shared dashboard sheet.
  useEffect(() => {
    const root = document.getElementById('react-root');
    const prev = root?.style.height ?? '';
    if (root) root.style.height = '100%';
    return () => {
      if (root) root.style.height = prev;
    };
  }, []);

  // body class = active tab name (changePrjAppViewState wipes all body classes).
  useEffect(() => {
    if (activeTab) document.body.className = activeTab;
    return () => {
      document.body.className = '';
    };
  }, [activeTab]);

  // Document title: '{Tab title}: {project name lowercased}' (ContainerHeaderView
  // sets the lowercased name; changePrjAppViewState prefixes the tab title).
  useEffect(() => {
    if (!project?.name) return;
    const name = String(project.name).toLowerCase();
    document.title = activeTab ? `${TAB_TITLES[activeTab]}: ${name}` : name;
  }, [project?.name, activeTab]);

  // Project fetch failure (bad id / no access) → /dashboard (create.model.project_main).
  useEffect(() => {
    if (projectError || !Number.isFinite(projectId)) {
      window.location.replace('/dashboard');
    }
  }, [projectError, projectId]);

  // Menu click → hash navigate (Backbone: Dispatcher open:X + history.navigate).
  // Re-clicking the active tab does not re-navigate (history.navigate to the
  // same fragment doesn't push an entry; avoids duplicate history states) —
  // but it is NOT a no-op in Backbone: MenuView fires
  // `Dispatcher.trigger('open:' + tabName)` on EVERY click, bypassing the
  // router entirely (project mvc/view/menu.js:46-52). Tab views subscribed to
  // that event therefore re-run when the ALREADY-ACTIVE rail item is clicked.
  // The only such views are dev-sync F's AHU Components / AHU Accessories
  // (ahu_components.js:41 / ahu_accessories.js:41 → `fetchAndRender`), so this
  // branch re-broadcasts the open as a DOM event they can hear (consumed by
  // `useRefetchOnTabReentry`). The other branch needs nothing — the hash change
  // activates the tab and the activation transition covers it. The branches are
  // mutually exclusive, so each open re-fetches EXACTLY once, matching the
  // single dispatcher trigger.
  const openTab = (value: string) => {
    if (location.hash !== `#${value}`) navigate({ hash: `#${value}` });
    else window.dispatchEvent(new CustomEvent(TAB_REOPEN_EVENT, { detail: value }));
  };

  const nonEditable = !!project && project.editable === false;

  // #workflow_popup_button (fn['bind.view.workflow_popup'], §1.9 / parity 99):
  // FRESH project fetch first, the popup opens only on fetch success.
  const onWorkflowClick = useCallback(() => {
    if (!Number.isFinite(projectId)) return;
    dispatch(
      projectBoardApi.endpoints.getProject.initiate(projectId, { forceRefetch: true })
    )
      .unwrap()
      .then(() => dispatch(openWorkflow()))
      .catch(() => undefined);
  }, [dispatch, projectId]);

  // Hold a blank frame until the route stylesheets have applied (see the
  // useStylesheets gate above). All hooks run first, so this only affects
  // paint; effects (body class, title, height chain) still prep the shell.
  if (!cssLoaded) return null;

  return (
    <>
      {/* Global empty popup mount (server shell parity). */}
      <div id="wrapper-div" className="popup popup-wrapper" />

      {/* Workflow Manager popup — legacy prepends it to <body>. */}
      {workflowOpen && (
        <WorkflowPopup
          projectId={projectId}
          onClose={() => dispatch(closeWorkflow())}
        />
      )}

      {/* Shared header — project variant (project-ksdm-header class +
          #workflow_popup_button → Workflow Manager popup, 3c). */}
      <GlobalHeader appName="project" onWorkflowClick={onWorkflowClick} />

      {/* Inline height = project skeleton.css:317's own value; out-ranks the
          leaked dashboard `body #ksdm-content` vh rule (height-chain effect). */}
      <div id="ksdm-content" style={{ height: 'calc(100% - 63px)' }}>
        {/* Left rail collapse toggle (main.tmpl) — collapse visuals are pure
            CSS off the checkbox; footer reflow handled via PageFooter classes. */}
        <input
          id="left_resizer"
          type="checkbox"
          className="left-menu-resizer whiteboard-resizer"
          checked={railCollapsed}
          onChange={() => dispatch(toggleRailCollapsed())}
        />
        <label htmlFor="left_resizer" className="ka-has-tooltip" tooltip="Project">
          <i className="icon-hamburger" />
          <h2>Project</h2>
          <i className="icon-angle-left ka-has-tooltip" tooltip="Collapse" />
        </label>

        <BoardMenu
          tabs={actualTabs}
          project={project}
          activeTab={activeTab}
          onOpen={openTab}
        />

        <div className="main-container col-xs-11 col-xs-offset-1">
          <BoardContainerHeader projectId={projectId} />
          <div
            id="container"
            className={`col-xs-12${nonEditable ? ' non-editable-project' : ''}`}
          >
            <BoardTabs projectId={projectId} activeTab={activeTab} />
          </div>
        </div>
      </div>

      {/* Footer (projectBase.html classes differ from the whiteboard footer);
          rail collapse: outer div loses col-xs-11/offset and gains .to-left
          (MainView move_footer, parity 3). */}
      <PageFooter
        outerClassName={
          railCollapsed ? 'menu-200 to-left' : 'col-xs-11 col-xs-offset-1 menu-200'
        }
        innerClassName="col-xs-12 footer-wrapper bottom-3"
      />
    </>
  );
};

export default ProjectBoardPage;
